import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { resetAudioList, addTrack, setAutoPlay, setIsPlaying as setPlayerIsPlaying } from '../../redux/slices/musicPlayerSlice';
import { setListContext, clearListContext } from '../../redux/slices/listContextSlice';
import { useToast } from '../../contexts/ToastContext';
import { silentUrlFor, resolveTrackSrc, sampleUrlOf } from '../../utils/resolvePlaybackSrc';
import { entityId } from '../../utils/entityId';
import { getCachedYoutubeId, resolveTrackLinkLazy, parseDurationSec } from '../../utils/lazyResolveCache';
import { isWeb, isSamplesOnly, isNative } from '../../utils/webOnly';
import { resolveCover } from '../../utils/cover';
import { getNativePlaybackEngine } from '../../playback/NativePlaybackEngine';
import { track as trackEvent } from '../../utils/analytics';

// Bucket a yt-dlp / extraction error string into a small, stable set of
// reasons for the `playback_fallback` event. Keep the buckets coarse — the
// point is a dashboard that can tell "YouTube changed something" (cdn_403 /
// no_formats spiking across many users) from "this user is on a flaky VPN"
// (network_blocked / rate_limit on a few installs).
export function classifyExtractError(msg) {
    const s = String(msg || '');
    if (!s) return 'unknown';
    if (/HTTP Error 403|status 403|rejected by CDN/i.test(s)) return 'cdn_403';
    // Age gate BEFORE the bot-challenge check: both are "Sign in to confirm
    // …" templates, but one is a per-video restriction and the other a
    // network throttle. Conflating them made age-gated tracks look like a
    // fleet-wide rate-limit wave in the playback_fallback alert.
    if (/sign in to confirm your age/i.test(s)) return 'age_restricted';
    if (/HTTP Error 429|too many requests|not a bot/i.test(s)) return 'rate_limit';
    if (/Requested format is not available|Only images are available|no playable stream|No stream URL/i.test(s)) return 'no_formats';
    if (/private video|video unavailable|has been removed|no longer available|not available in your country|geo/i.test(s)) return 'unavailable';
    if (/network|ENOTFOUND|ECONNRESET|ETIMEDOUT|timed out|Unable to download webpage/i.test(s)) return 'network';
    if (/not currently supported|\.mpd/i.test(s)) return 'dash_only';
    return 'other';
}

let currentDjpalTrackId = null;
let currentDjpalPaused = false;
// Global counter to cancel stale extractions when user clicks a different track
let playRequestCounter = 0;

// Lazy-resolve UX budgets. Samples always work, so a slow YT path falls
// back rather than failing.
//   LAZY_SEARCH_TIMEOUT_MS: hard cap on the resolve IPC alone (YT search +
//   server rescore + insert). If exceeded → sample. Slow searches keep
//   running in the background and seed the cache for the next click.
//   LAZY_TOTAL_BUDGET_MS:   hard cap on resolve + yt-dlp extraction
//   combined. If exceeded mid-extraction → sample. We bump
//   playRequestCounter so the orphaned extractStreamUrl bails on its
//   post-await check instead of swapping audio mid-sample.
//
// Budget sized to cover the yt-dlp fallback path. yt-dlp's default
// (android_vr) sometimes hands us URLs the CDN immediately 403s; main.js
// catches that via a HEAD validate, throws the URL away, and retries
// with the deno + multi-client fallback (`web_safari,default,tv,ios`)
// which adds ~6s. Worst case: 5s search + 3s default-client + 6s
// fallback ≈ 14s, so 20s leaves a small margin. The previous 10s ceiling
// guaranteed sample-fallback whenever yt-dlp needed the retry — which
// was happening on a meaningful chunk of HIGH matches.
const LAZY_SEARCH_TIMEOUT_MS = 5000;
const LAZY_TOTAL_BUDGET_MS = 20000;
const _LAZY_TIMEOUT_SENTINEL = Symbol('lazy_timeout');

// What each preview is doing right now, by previewId, so a page's hero Play
// button can show the row it plays loading and then playing (it starts that
// row with a djpal-row-play event and otherwise never hears back).
const previewStates = new Map();
const PREVIEW_STATE_EVENT = 'djpal-preview-state';
export function usePreviewState(previewId) {
    const [state, setState] = useState(() => previewStates.get(previewId) || { loading: false, playing: false });
    useEffect(() => {
        setState(previewStates.get(previewId) || { loading: false, playing: false });
        const on = (e) => { if (e.detail.previewId === previewId) setState({ loading: e.detail.loading, playing: e.detail.playing }); };
        window.addEventListener(PREVIEW_STATE_EVENT, on);
        return () => window.removeEventListener(PREVIEW_STATE_EVENT, on);
    }, [previewId]);
    return state;
}

const DJPalAudioPreview = ({ src, youtubeId, soundcloudUrl, trackId, durationSec, trackName, trackArtist, trackArtists, trackCover, pageUrl, previewId, playContext, playbackBlocked = false, onPlayingChange, children }) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const djpalToken = useSelector((state) => state?.djpal?.token || '');
    // NATIVE: what the plugin is actually playing. jinke's <audio> element is
    // NOT that — on native it stays parked on the first track the user tapped
    // while the plugin auto-advances past it, so any "am I the active track?"
    // test based on that element goes stale after one advance. The plugin
    // reports title/artist on every playerState, which Redux mirrors here.
    //
    // Selected as PRIMITIVES, not the currentTrack object: setNativePlayerState
    // rebuilds that object on every playerState tick (4Hz while playing), so
    // subscribing to it would re-render every row in the feed four times a
    // second. Strings compare by value and only fire on a real track change.
    const playerCurrentName = useSelector((state) => state.musicPlayer.currentTrack?.name || '');
    const playerCurrentSinger = useSelector((state) => state.musicPlayer.currentTrack?.singer || '');
    const playerIsPlaying = useSelector((state) => state.musicPlayer.isPlaying);
    // doPlay is a useCallback whose dep list deliberately stays small; read
    // these through refs so it sees fresh values without being rebuilt (and
    // without re-binding the djpal-row-play listener) on every change.
    const playerCurrentNameRef = useRef(playerCurrentName);
    const playerCurrentSingerRef = useRef(playerCurrentSinger);
    const playerIsPlayingRef = useRef(playerIsPlaying);
    playerCurrentNameRef.current = playerCurrentName;
    playerCurrentSingerRef.current = playerCurrentSinger;
    playerIsPlayingRef.current = playerIsPlaying;
    const [isPlaying, setIsPlaying] = useState(() => {
        // Restore playing state on mount if this track is currently playing
        return currentDjpalTrackId === previewId && !currentDjpalPaused;
    });
    const [isLoading, setIsLoading] = useState(false);

    // Local override that wins over the youtubeId prop. Seeded from the
    // shared lazyResolveCache (in case a prior click on this same trackId
    // — anywhere in the app — already resolved it). Survives parent
    // re-renders; the cache is module-scoped so a remounted instance
    // also picks it up.
    const [resolvedYoutubeId, setResolvedYoutubeId] = useState(() => getCachedYoutubeId(trackId));

    // Listen for resolves from sibling instances rendering the same trackId.
    useEffect(() => {
        if (!trackId) return undefined;
        const tid = String(trackId);
        const onResolved = (e) => {
            if (e?.detail?.trackId === tid && e.detail.youtubeId) {
                setResolvedYoutubeId(e.detail.youtubeId);
            }
        };
        window.addEventListener('djpal-track-link-resolved', onResolved);
        return () => window.removeEventListener('djpal-track-link-resolved', onResolved);
    }, [trackId]);

    const effectiveYoutubeId = resolvedYoutubeId || youtubeId;

    // Determine the full-play URL (YouTube preferred over SoundCloud).
    // When the track is on the manual takedown list we never expose a
    // full-play URL — every code path that branches on `fullPlayUrl` /
    // `hasFullPlay` (click handler, lazy resolve trigger, player-track-
    // change matcher) will then route the user to sample playback.
    const fullPlayUrl = playbackBlocked
        ? null
        : (effectiveYoutubeId && effectiveYoutubeId !== '-1'
            ? `https://www.youtube.com/watch?v=${effectiveYoutubeId}`
            : soundcloudUrl || null);

    const hasFullPlay = !!fullPlayUrl;
    // Play-icon visibility safety net. Desktop: icon renders for sample OR
    // full link. Web/mobile: full playback isn't supported, so the icon
    // only renders when a sample exists — a YT/SC-only track must not
    // appear playable. Lazy resolve never adds an icon to a cold track.
    // On samples-only platforms (web, iOS, Android) a track is playable only
    // if it has a sampleUrl/previewUrl. On Electron desktop, a track is
    // playable if it has either a sample OR a full-play URL (yt-dlp extracts).
    const hasSrc = isSamplesOnly ? !!src : (hasFullPlay || !!src);

    // Run the lazy YouTube-link resolve and AWAIT the result. Delegates to
    // the shared lazyResolveCache so the autoplay path + FeedWaveform see
    // the same cache. Returns `{youtubeId, ...}` or null when resolve isn't
    // applicable.
    const resolveTrackLinkNow = useCallback(async () => {
        if (!trackId) return null;
        if (effectiveYoutubeId && effectiveYoutubeId !== '-1') {
            return { youtubeId: effectiveYoutubeId, cached: true };
        }
        if (!src) return null;  // sample-fallback safety net — never resolve cold tracks
        return resolveTrackLinkLazy(
            {
                id: trackId,
                artist: trackArtist || '',
                title: trackName || '',
                // parseDurationSec, NOT Number(): mounts pass whatever the API
                // row carries, which is usually the "4:29" text — Number()
                // NaN'd it and the resolve snapshot silently lost duration.
                duration: parseDurationSec(durationSec),
                sampleUrl: src,
            },
            { token: djpalToken || null },
        );
    }, [trackId, src, effectiveYoutubeId, trackArtist, trackName, durationSec, djpalToken]);

    useEffect(() => {
        const handleStop = (e) => {
            if (e.detail === previewId) {
                setIsPlaying(false);
                setIsLoading(false);
            }
        };
        const handleStopAll = () => {
            setIsPlaying(false);
            setIsLoading(false);
        };
        const handlePlay = (e) => {
            if (e.detail === previewId) setIsPlaying(true);
        };
        window.addEventListener('djpal-track-stop', handleStop);
        window.addEventListener('djpal-track-stop-all', handleStopAll);
        window.addEventListener('djpal-track-play', handlePlay);
        return () => {
            window.removeEventListener('djpal-track-stop', handleStop);
            window.removeEventListener('djpal-track-stop-all', handleStopAll);
            window.removeEventListener('djpal-track-play', handlePlay);
        };
    }, [previewId]);

    // When the parent reuses this component for a different track (e.g.
    // user navigates to another artist page, so the row at index `i` of the
    // tracks-played-by-others list now represents a new track but the React
    // instance is preserved because the `key` is positional), local
    // `isPlaying=true` from the previous track lingers as a false positive —
    // no `player-track-change` event fires during navigation since the audio
    // keeps playing the original track. Re-verify against the actual player
    // whenever the represented track changes.
    useEffect(() => {
        if (trackId == null) return;
        const audio = window.musicPlayerInstance;
        if (!audio || audio.paused) {
            setIsPlaying(false);
            return;
        }
        const playerSrc = audio.src || '';
        const playerOriginalUrl = window.__currentPlayerOriginalUrl || null;
        const isThisActive =
            (fullPlayUrl && playerOriginalUrl === fullPlayUrl) ||
            (src && (playerSrc === src || playerSrc.includes(encodeURIComponent(src)))) ||
            // Id match covers a dead DB link replaced at play time: the
            // player streams a DIFFERENT url than this row derives, so both
            // url checks above fail even though it IS this track playing.
            (window.__currentPlayerTrackId != null && window.__currentPlayerTrackId === String(trackId));
        setIsPlaying(!!isThisActive);
    }, [trackId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync play/pause state with the global music player (handles Play All and external track changes)
    useEffect(() => {
        const handler = (e) => {
            const { musicSrc, originalUrl, playing, trackId: activeTrackId } = e.detail;
            const matches = (src && musicSrc === src)
                || (fullPlayUrl && originalUrl === fullPlayUrl)
                // Dead DB link replaced at play time — see the mount check above.
                || (trackId != null && activeTrackId != null && activeTrackId === String(trackId));
            if (matches) {
                setIsPlaying(playing);
                // Once the player confirms our track is playing, the loading
                // phase is over — clear the spinner so the play/pause icon
                // takes over. Belt-and-suspenders: doPlay also clears
                // isLoading on its own, but the snippet fallback path used
                // to leave it stuck.
                if (playing) {
                    setIsLoading(false);
                    currentDjpalTrackId = previewId;
                    currentDjpalPaused = false;
                } else {
                    currentDjpalPaused = true;
                }
            } else if (playing) {
                // A different track started — clear our playing state
                setIsPlaying(false);
                setIsLoading(false);
            }
        };
        window.addEventListener('player-track-change', handler);
        return () => window.removeEventListener('player-track-change', handler);
    }, [previewId, src, fullPlayUrl, trackId]);

    // Surface playing state to the parent (e.g. the feed card shows a
    // play/pause overlay on the cover art on mobile, where the inline
    // play button is hidden). No-op when no callback is passed.
    useEffect(() => {
        if (onPlayingChange) onPlayingChange(isPlaying);
    }, [isPlaying, onPlayingChange]);

    useEffect(() => {
        if (!previewId) return undefined;
        const detail = { previewId, loading: isLoading, playing: isPlaying };
        previewStates.set(previewId, detail);
        window.dispatchEvent(new CustomEvent(PREVIEW_STATE_EVENT, { detail }));
        return undefined;
    }, [previewId, isLoading, isPlaying]);
    // Unmounted rows stop being anything (the hero button falls back to Play).
    useEffect(() => () => {
        if (!previewId) return;
        previewStates.delete(previewId);
        window.dispatchEvent(new CustomEvent(PREVIEW_STATE_EVENT, { detail: { previewId, loading: false, playing: false } }));
    }, [previewId]);

    const playWithStreamUrl = useCallback(async (url, requestId, onError = null) => {
        setIsLoading(true);
        // Tell the bottom player to spin its play-button icon while we extract.
        // Cleared in `finally` regardless of success/failure/cancellation.
        window.dispatchEvent(new CustomEvent('djpal-track-extract-start'));

        try {
            // Unified path: yt-dlp downloads the track once to a local file
            // (with full session context → no googlevideo 403), which we then
            // play via the local audio server (Express sendFile → Range/seek
            // support) and decode for the waveform. One download, one outcome —
            // playback and the waveform can no longer disagree.
            const result = await window.electron.djpal.getTrackAudio(url);

            // Check if this request is still current (user may have clicked another track)
            if (requestId !== playRequestCounter) {
                return false;
            }

            if (result.error || !result.filePath) {
                console.warn('[DJPalAudioPreview] Track audio download failed:', result.error);
                // Surface the error to the caller so doPlay can attempt
                // replacement → sample → toast in that order. Don't toast here
                // (it would race the replacement IPC).
                if (onError) onError(result.error || 'no audio file');
                return null;
            }

            const isHls = false;
            const musicSrc = `http://localhost:3069/audio?path=${encodeURIComponent(result.filePath)}`;

            dispatch(resetAudioList());
            dispatch(addTrack({
                name: trackName || 'Unknown Track',
                singer: trackArtist || '',
                cover: resolveCover(trackCover),
                musicSrc,
                isHls,
                ...(isHls ? { hlsSrc: result.streamUrl } : {}),
                // Store original URL for yt-dlp waveform generation. Use the
                // `url` arg (the actual URL we just extracted), NOT the
                // outer-scope `fullPlayUrl` — those diverge in the lazy-
                // resolve branch where fullPlayUrl is still null at the
                // time playWithStreamUrl runs (React hasn't re-rendered
                // with the resolved youtubeId yet). Passing null here broke
                // peaks lookup (60s fallback → 3min decode of raw audio
                // bytes) and FeedWaveform's active-track match (no cyan
                // progress) for clicked sample-only tracks.
                originalUrl: url,
                ...(pageUrl ? { pageUrl } : {}),
                // Store sample so onAudioError can fall back to it if the stream 403s
                samplePreviewUrl: src || null,
                // Carry the public id forward so the now-playing player's
                // LikeButton can write it back to user_likes. Without this,
                // a like fired from the player widget creates a row with
                // track_public_id=null AND preview_url=null, which the
                // likes sidebar then renders as unplayable.
                trackId: trackId || null,
                ...(Array.isArray(trackArtists) && trackArtists.length ? { artists: trackArtists } : {}),
            }));
            dispatch(setAutoPlay(true));
            if (playContext) {
                dispatch(setListContext({ items: playContext.items, currentIndex: playContext.index, type: playContext.type, origin: playContext.origin }));
            } else {
                dispatch(clearListContext());
            }
            // Explicit play kick — Chromium's gesture activation can age out
            // by the time jinke's autoPlay prop processes the new audioList
            // after a long async chain (lazy resolve + yt-dlp extract often
            // spans 2-4s). When that happens jinke silently skips the call,
            // leaving audio.paused=true while the row's local isPlaying flag
            // (set synchronously on click) stays true — visible as "card
            // shows pause, bottom player shows play, nothing audible, second
            // click works." 500ms gives jinke time to swap audio.src; HLS
            // path skipped because hls.js handles its own play() via
            // MANIFEST_PARSED. Safe to call even if jinke already started —
            // play() on a playing element is a no-op.
            if (!isHls) {
                setTimeout(() => {
                    if (requestId !== playRequestCounter) return;
                    const a = window.musicPlayerInstance;
                    if (a && a.paused) a.play?.()?.catch(() => {});
                }, 500);
            }
            return true;
        } catch (err) {
            console.error('[DJPalAudioPreview] Extract error:', err);
            if (requestId !== playRequestCounter) return false;
            // Same contract as a clean yt-dlp failure: null = "no stream,
            // caller decides" (sample / replacement / skip-ahead). Returning
            // false here used to swallow the failure before doPlay could
            // advance the feed.
            if (onError) onError(err?.message || String(err));
            return null;
        } finally {
            if (requestId === playRequestCounter) {
                setIsLoading(false);
            }
            window.dispatchEvent(new CustomEvent('djpal-track-extract-end'));
        }
    }, [dispatch, toast, src, trackName, trackArtist, trackArtists, trackCover, trackId, playContext]);

    const playSnippet = useCallback(() => {
        dispatch(resetAudioList());
        dispatch(addTrack({
            name: trackName || 'Unknown Track',
            singer: trackArtist || '',
            cover: resolveCover(trackCover),
            musicSrc: src,
            // Mirror the full-play branch so the now-playing player's
            // LikeButton can write the right fields back to user_likes
            // (otherwise a like fired from the player widget for a
            // sample-only track creates a row with all nulls →
            // unplayable in the likes sidebar).
            samplePreviewUrl: src || null,
            trackId: trackId || null,
            ...(pageUrl ? { pageUrl } : {}),
            ...(Array.isArray(trackArtists) && trackArtists.length ? { artists: trackArtists } : {}),
        }));
        dispatch(setAutoPlay(true));
        if (playContext) {
            dispatch(setListContext({ items: playContext.items, currentIndex: playContext.index, type: playContext.type, origin: playContext.origin }));
        } else {
            dispatch(clearListContext());
        }
    }, [dispatch, src, trackName, trackArtist, trackArtists, trackCover, trackId, pageUrl, playContext]);

    // A manually clicked track that dies at extraction time (no sample, no
    // working replacement) used to dead-end: setListContext only runs on
    // success, so the player had no context and nothing advanced. The
    // auto-advance path (MusicPlayer.advanceListContext) silently skips
    // unresolvable items; a click inside the same feed must behave the same
    // way — walk the rest of playContext, resolve the first playable item,
    // and start it exactly like a click on that row would (resetAudioList +
    // addTrack + setAutoPlay + setListContext). Returns true if playback
    // moved on. Desktop only — native's queue is the surface, and the
    // samples-only platforms never reach the extraction failure paths.
    const advancePastFailure = useCallback(async (requestId) => {
        const items = playContext?.items;
        if (!Array.isArray(items) || !items.length || playContext.type !== 'tracks') return false;
        for (let i = playContext.index + 1; i < items.length; i++) {
            if (requestId !== playRequestCounter) return false;
            const item = items[i];
            if (!item || item._type === 'mix') continue;
            // Start the target row's loader now (same as advanceListContext).
            window.dispatchEvent(new CustomEvent('feed-row-preload', {
                detail: { src: sampleUrlOf(item), trackId: entityId(item) },
            }));
            let resolved = null;
            try {
                resolved = await Promise.race([
                    resolveTrackSrc(item),
                    new Promise((r) => setTimeout(() => r(null), 30000)),
                ]);
            } catch (e) {
                console.warn('[DJPalAudioPreview] advancePastFailure: resolve threw', e?.message);
            }
            if (requestId !== playRequestCounter) return false;
            if (!resolved?.musicSrc) {
                console.warn('[DJPalAudioPreview] advancePastFailure: unresolved, skipping', { i, title: item?.title });
                continue;
            }
            dispatch(resetAudioList());
            dispatch(addTrack({
                name: item.title || 'Unknown Track',
                singer: item.artist || '',
                cover: resolveCover(item.artwork, item.artworkUrl, item.image, item.cover),
                musicSrc: resolved.musicSrc,
                trackId: entityId(item),
                samplePreviewUrl: sampleUrlOf(item),
                ...(resolved.isHls ? { isHls: true, hlsSrc: resolved.hlsSrc } : {}),
                ...(resolved.originalUrl ? { originalUrl: resolved.originalUrl } : {}),
                ...(Array.isArray(item.artists) && item.artists.length ? { artists: item.artists } : {}),
            }));
            dispatch(setAutoPlay(true));
            dispatch(setListContext({ items, currentIndex: i, type: playContext.type, origin: playContext.origin }));
            return true;
        }
        return false;
    }, [dispatch, playContext]);

    // Terminal extraction failure with no sample: say why, then try to keep
    // the feed going. Owns the toasts so callers don't double up.
    const failNoSample = useCallback(async (requestId) => {
        setIsLoading(false);
        setIsPlaying(false);
        currentDjpalTrackId = null;
        const canAdvance = Array.isArray(playContext?.items)
            && playContext.type === 'tracks'
            && playContext.index < playContext.items.length - 1;
        if (!canAdvance) {
            toast.error('Unable to play this track. The audio source is unavailable.');
            return false;
        }
        // Immediate feedback — the resolve below can take seconds.
        toast.warning(`Couldn’t play ${trackName || 'this track'} — skipping to the next track.`, 5000);
        window.dispatchEvent(new CustomEvent('djpal-track-extract-start'));
        try {
            const advanced = await advancePastFailure(requestId);
            if (!advanced && requestId === playRequestCounter) {
                console.warn('[DJPalAudioPreview] failNoSample: nothing playable after this track');
            }
            return advanced;
        } finally {
            window.dispatchEvent(new CustomEvent('djpal-track-extract-end'));
        }
    }, [toast, trackName, playContext, advancePastFailure]);

    const doPlay = useCallback(async () => {
        if (!hasSrc) {
            toast.error('No audio preview available for this track');
            return false;
        }

        // This track is the one currently active in the global player
        // (works even when the same track is rendered in multiple places —
        // e.g. feed + likes sidebar — since it relies on player state, not previewId).
        const playerOriginalUrl = window.__currentPlayerOriginalUrl || null;
        const audio = window.musicPlayerInstance;
        const playerSrc = audio?.src || '';

        // NATIVE: ask the plugin's state, not jinke's element.
        //
        // jinke's <audio> keeps the FIRST tapped track's src forever while the
        // plugin auto-advances past it. So after playing a few tracks, tapping
        // row 1 matched "this row is already active" (its url still equals
        // jinke's src) and fell into the pause/resume branch below — which
        // toggled whatever the plugin was really on. Reported as: tap track
        // 1's artwork and it just start/stops track 5 with every click.
        //
        // Match on the plugin's reported title+artist, the same pair FeedTab's
        // now-playing overlay uses.
        const isThisActive = isNative
            ? (!!playerCurrentNameRef.current
                && playerCurrentNameRef.current === (trackName || '')
                && playerCurrentSingerRef.current === (trackArtist || ''))
            : ((fullPlayUrl && playerOriginalUrl === fullPlayUrl)
                || (src && (playerSrc === src || playerSrc.includes(encodeURIComponent(src)))));

        // NATIVE + this row IS the playing track: drive the plugin directly.
        // Routing through jinke's element would resume the wrong thing —
        // its src is the old track, so onAudioPlay would issue a playIndex
        // for THAT instead of resuming what's loaded.
        if (isNative && isThisActive) {
            const engine = getNativePlaybackEngine();
            if (engine.isAvailable()) {
                const nowPlaying = !!playerIsPlayingRef.current;
                if (nowPlaying) {
                    engine.pause();
                    dispatch(setPlayerIsPlaying(false));
                    dispatch(setAutoPlay(false));
                } else {
                    engine.play();
                    dispatch(setPlayerIsPlaying(true));
                    dispatch(setAutoPlay(true));
                }
                setIsPlaying(!nowPlaying);
                currentDjpalPaused = nowPlaying;
                currentDjpalTrackId = previewId;
                window.dispatchEvent(new CustomEvent('player-track-change', {
                    detail: { musicSrc: src || null, originalUrl: fullPlayUrl, playing: !nowPlaying },
                }));
                return true;
            }
        }

        // Currently playing this track -> pause
        if (isThisActive && audio && !audio.paused) {
            audio.pause();
            setIsPlaying(false);
            currentDjpalPaused = true;
            // Broadcast so any other DJPalAudioPreview instances showing the
            // same track (e.g. artist page + likes sidebar) flip to paused.
            window.dispatchEvent(new CustomEvent('player-track-change', {
                detail: { musicSrc: src || null, originalUrl: fullPlayUrl, playing: false },
            }));
            return true;
        }

        // This track is loaded but paused -> resume
        if (isThisActive && audio && audio.paused) {
            const playPromise = audio.play();
            if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(err => {
                    console.warn('[DJPalAudioPreview] Resume play failed:', err);
                    toast.error('Unable to play this preview');
                    setIsPlaying(false);
                });
            }
            setIsPlaying(true);
            currentDjpalPaused = false;
            window.dispatchEvent(new CustomEvent('player-track-change', {
                detail: { musicSrc: src || null, originalUrl: fullPlayUrl, playing: true },
            }));
            return true;
        }

        // Different track or fresh — claim this request
        const requestId = ++playRequestCounter;

        // Stop all other track previews (not just the previous one).
        // The stop-all handler also fires on THIS component, so set our own
        // state AFTER the dispatch — otherwise it resets our flip.
        window.dispatchEvent(new CustomEvent('djpal-track-stop-all'));
        // Tell mix playback to clear its active state
        window.dispatchEvent(new CustomEvent('djpal-mix-stop'));
        currentDjpalPaused = false;
        currentDjpalTrackId = previewId;

        // Instant click feedback: flip the row icon to "loading" + "playing"
        // synchronously so the user sees their click registered. Reset by
        // error paths if extraction fails or by stop events for other tracks.
        setIsLoading(true);
        setIsPlaying(true);

        // Samples-only platforms (iOS, Android, web, mobile web) skip
        // every full-stream / lazy-resolve attempt and play the Apple
        // sample directly. yt-dlp and the resolve IPC are Electron-desktop
        // only; trying either here just delays the click handler before
        // failing into the same playSnippet path.
        if (isSamplesOnly) {
            setIsLoading(false);
            if (src) {
                playSnippet();
            } else {
                setIsPlaying(false);
                currentDjpalTrackId = null;
                toast.error('No audio preview available for this track');
                return false;
            }
        } else if (hasFullPlay) {
            // Capture the yt-dlp error string so we can pass it to the
            // replacement IPC for the rate-limit-vs-deleted classifier.
            let extractError = null;
            const result = await playWithStreamUrl(fullPlayUrl, requestId, (err) => {
                extractError = err;
            });
            // Check if still current after async
            if (requestId !== playRequestCounter) return false;
            if (result === null) {
                // Stream extraction failed but we have a sample. Before
                // giving up to the sample, try replacement: maybe the
                // existing youtube_id is dead (deleted from YT since the
                // batch enricher wrote it) and a fresh search will find
                // a working alternative. The IPC handles oembed
                // verification + rate-limit shortcut internally; if the
                // video still exists OR we're rate-limited, IPC returns
                // null and we silently sample.
                let replaced = false;
                // No `effectiveYoutubeId` requirement: SoundCloud-only tracks
                // (no DB youtube_id) can also have their full-play URL die —
                // when that happens, send sentinel `-1` so the IPC skips
                // oembed-verify (there's no real video to check) and goes
                // straight to a YT search.
                if (trackId && extractError
                    && window.electron?.djpal?.resolveTrackLink) {
                    const replaceId = effectiveYoutubeId || '-1';
                    try {
                        const repl = await window.electron.djpal.resolveTrackLink({
                            trackId,
                            artist: trackArtist || '',
                            title: trackName || '',
                            durationSec: parseDurationSec(durationSec),
                            hasAppleId: !!src,
                            token: djpalToken || null,
                            replaceVideoId: replaceId,
                            ytdlpError: extractError,
                        });
                        if (requestId !== playRequestCounter) {
                            return false;
                        }
                        const newId = repl?.youtubeId || null;
                        if (newId && newId !== effectiveYoutubeId) {
                            // Got a fresh id — store in cache so subsequent
                            // clicks bypass the dead-id retry path entirely.
                            try {
                                const { setCachedYoutubeId } = require('../../utils/lazyResolveCache');
                                setCachedYoutubeId(trackId, newId);
                            } catch {}
                            // Try playing the new URL. If it ALSO fails,
                            // fall through to sample (no infinite loop —
                            // we pass no onError this time so the
                            // replacement isn't retried).
                            const newUrl = `https://www.youtube.com/watch?v=${newId}`;
                            const newRes = await playWithStreamUrl(newUrl, requestId);
                            if (requestId !== playRequestCounter) return false;
                            if (newRes !== null && newRes !== false) replaced = true;
                        }
                    } catch (e) {
                        console.warn('[DJPalAudioPreview] replacement attempt failed:', e?.message);
                    }
                }
                if (!replaced) {
                    setIsLoading(false);
                    // No sample, no working YT replacement (tracks with no
                    // Apple preview whose youtube_id is dead / age-gated /
                    // taken down). Toast why and, inside a feed context,
                    // move on to the next playable item — otherwise stop.
                    // Early return is critical: the function falls through
                    // to a `setIsPlaying(true)` at the bottom, which would
                    // clobber the reset and leave the row stuck "playing".
                    let advanced = false;
                    if (!src) advanced = await failNoSample(requestId);
                    // Fleet-wide early warning: a track that HAS a full-play
                    // link could not be streamed. A rising cdn_403/no_formats
                    // share across installs = YouTube changed something and
                    // yt-dlp needs a bump (see docs/analytics-events.md).
                    trackEvent('playback_fallback', {
                        reason: classifyExtractError(extractError),
                        fell_back_to: src ? 'sample' : (advanced ? 'skip' : 'error'),
                        had_link: true,
                        link_kind: effectiveYoutubeId ? 'youtube' : 'other',
                        replacement_tried: !!(trackId && extractError),
                        from: playContext?.type || null,
                    });
                    if (src) {
                        // Apple sample available — fall back to that.
                        playSnippet();
                    } else {
                        return false;
                    }
                }
            } else if (!result) {
                setIsLoading(false);
                setIsPlaying(false);
                currentDjpalTrackId = null;
                return false;
            }
        } else if (!playbackBlocked && trackId && src && window.electron?.djpal?.resolveTrackLink) {
            // Lazy-resolve path: track has a sample but no DB youtube_id.
            // 1) Ask the server (via IPC) for a HIGH-confidence YT match,
            //    bounded by LAZY_SEARCH_TIMEOUT_MS.
            // 2) If we got one, try to extract+play it via yt-dlp, bounded
            //    by the remainder of LAZY_TOTAL_BUDGET_MS.
            // 3) On either timeout OR failure (no HIGH match, yt-dlp error,
            //    DASH-only, etc.) fall back to the sample.
            // Slow searches keep running in the background and seed the
            // cache so the next click goes straight to YT.
            const lazyStartedAt = Date.now();
            const resolveResult = await Promise.race([
                resolveTrackLinkNow(),
                new Promise((resolve) => setTimeout(() => resolve(_LAZY_TIMEOUT_SENTINEL), LAZY_SEARCH_TIMEOUT_MS)),
            ]);
            if (requestId !== playRequestCounter) return false;
            const ytId = (resolveResult && resolveResult !== _LAZY_TIMEOUT_SENTINEL)
                ? (resolveResult.youtubeId || null)
                : null;

            if (!ytId) {
                // No HIGH match, search timed out, or IPC errored — sample.
                setIsLoading(false);
                playSnippet();
            } else {
                const remainingBudget = Math.max(0, LAZY_TOTAL_BUDGET_MS - (Date.now() - lazyStartedAt));
                const ytUrl = `https://www.youtube.com/watch?v=${ytId}`;
                let lazyExtractError = null;
                const streamResult = await Promise.race([
                    playWithStreamUrl(ytUrl, requestId, (err) => { lazyExtractError = err; }),
                    new Promise((resolve) => setTimeout(() => resolve(_LAZY_TIMEOUT_SENTINEL), remainingBudget)),
                ]);
                if (requestId !== playRequestCounter) return false;
                if (streamResult === _LAZY_TIMEOUT_SENTINEL) {
                    // Extraction blew the total budget. Invalidate the
                    // in-flight extractStreamUrl (its post-IPC check on
                    // playRequestCounter will fail and it'll abort the
                    // dispatch) before we play the sample, so the orphaned
                    // resolve doesn't swap audio mid-snippet.
                    ++playRequestCounter;
                    setIsLoading(false);
                    trackEvent('playback_fallback', {
                        reason: 'timeout',
                        fell_back_to: 'sample',
                        had_link: false,
                        link_kind: 'youtube',
                        replacement_tried: false,
                        from: playContext?.type || null,
                    });
                    playSnippet();
                } else if (streamResult === null) {
                    // yt-dlp gave up cleanly (403, DASH-only, etc.) within
                    // budget — sample fallback.
                    setIsLoading(false);
                    trackEvent('playback_fallback', {
                        reason: classifyExtractError(lazyExtractError),
                        fell_back_to: 'sample',
                        had_link: false,
                        link_kind: 'youtube',
                        replacement_tried: false,
                        from: playContext?.type || null,
                    });
                    playSnippet();
                } else if (!streamResult) {
                    setIsLoading(false);
                    setIsPlaying(false);
                    currentDjpalTrackId = null;
                    return false;
                }
            }
        } else {
            // No full-play URL and no resolve possible (no trackId, or
            // web/mobile build with no Electron IPC). Sample-only path —
            // unchanged from pre-feature behavior.
            setIsLoading(false);
            playSnippet();
        }

        setIsPlaying(true);
        return true;
    }, [toast, hasSrc, hasFullPlay, fullPlayUrl, src, previewId, trackId, playWithStreamUrl, playSnippet, resolveTrackLinkNow, playContext, failNoSample]);

    // Safety net around doPlay. Its body has no outer catch — it clears
    // isLoading/isPlaying by hand at each of its ~10 exits — so a throw would
    // strand the row on its spinner with no way back, which is exactly the
    // shape of the bottom-player loader bug. Not reachable today (every await
    // in there swallows its own rejection), but one unguarded await added
    // later is all it takes, and a stuck spinner gives the user nothing.
    const doPlaySafe = useCallback(async () => {
        try {
            return await doPlay();
        } catch (err) {
            console.error('[DJPalAudioPreview] play threw:', err);
            setIsLoading(false);
            setIsPlaying(false);
            currentDjpalTrackId = null;
            toast.error('Couldn’t start playback. Please try again.');
            return false;
        }
    }, [doPlay, toast]);

    const handleClick = useCallback((e) => {
        e.stopPropagation();
        doPlaySafe();
    }, [doPlaySafe]);

    // Listen for row-level play requests
    useEffect(() => {
        const handleRowPlay = (e) => {
            if (e.detail === previewId) doPlaySafe();
        };
        window.addEventListener('djpal-row-play', handleRowPlay);
        return () => window.removeEventListener('djpal-row-play', handleRowPlay);
    }, [previewId, doPlaySafe]);

    return (
        <div className={`djpal-audio-preview ${isPlaying ? 'playing' : ''} ${!hasSrc ? 'no-preview' : ''}`} onClick={handleClick}>
            {children}
            {hasSrc && (
                <div className="play-overlay">
                    {isLoading ? (
                        <span className="play-spinner" aria-hidden="true" />
                    ) : (
                        <span className={`play-icon ${isPlaying ? 'paused' : ''}`} aria-hidden="true">
                            {isPlaying ? (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                    <rect x="6" y="4" width="4" height="16" rx="1" />
                                    <rect x="14" y="4" width="4" height="16" rx="1" />
                                </svg>
                            ) : (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                    <polygon points="6 3 20 12 6 21 6 3" />
                                </svg>
                            )}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default DJPalAudioPreview;
